import React, { useState, useEffect } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from '../Common/AuthField';
import { Layout } from './Layout';
import { Button, Divider, Header, Message, Segment, Grid, List } from 'semantic-ui-react';
import * as yup from 'yup';
import { userManageClient } from '../userManageClient';
import { EnableAuthenticatorRequest, AuthenticatorSetupResponse } from '@app/api2/api';
import { QRCodeSVG } from 'qrcode.react';
import { buildUrl, AppRoutes } from '../../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface EnableAuthenticatorFormValues {
    code: string;
}

export const EnableAuthenticator: React.FC = () => {
    const [setupInfo, setSetupInfo] = useState<AuthenticatorSetupResponse | null>(null);
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [enableError, setEnableError] = useState<string>('');

    const initialValues: EnableAuthenticatorFormValues = {
        code: ''
    };

    const validationSchema: yup.SchemaOf<EnableAuthenticatorFormValues> = yup.object().shape({
        code: yup.string()
            .min(6, 'The code must be at least 6 characters long')
            .max(7, 'The code must be at most 7 characters long')
            .required('Verification code is required')
    });

    useEffect(() => {
        loadSetupInfo();
    }, []);

    const loadSetupInfo = async () => {
        try {
            setLoading(true);
            const result = await userManageClient.getAuthenticatorSetup();
            setSetupInfo(result);
        } catch (error) {
            console.error('Error loading authenticator setup:', error);
            setEnableError('Error loading authenticator setup information.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: EnableAuthenticatorFormValues) => {
        setEnableError('');

        try {
            const request: EnableAuthenticatorRequest = {
                code: values.code
            };

            const result = await userManageClient.enableAuthenticator(request);

            if (result.success && result.recoveryCodes) {
                setRecoveryCodes(result.recoveryCodes);
            } else {
                setEnableError(result.errors?.join(', ') || 'Invalid verification code.');
            }
        } catch (error) {
            console.error('Enable authenticator error:', error);
            setEnableError('An error occurred while enabling the authenticator. Please try again.');
        }
    };

    if (loading) {
        return (
            <Layout>
                <Header as="h1">Configure authenticator app</Header>
                <Segment loading>
                    <div style={{ height: '200px' }} />
                </Segment>
            </Layout>
        );
    }

    if (recoveryCodes) {
        return (
            <Layout>
                <Header as="h1">Configure authenticator app</Header>

                <Message success>
                    <Message.Header>Your authenticator app has been verified.</Message.Header>
                    <p>Your two-factor authentication app has been verified. When you log in, you will be asked for a code from this app.</p>
                </Message>

                <Segment>
                    <Header as="h3">Recovery codes</Header>
                    <p>
                        <strong>Put these codes in a safe place.</strong><br />
                        If you lose your device and don't have the recovery codes you will lose access to your account.
                    </p>

                    <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', fontFamily: 'monospace' }}>
                        {recoveryCodes.map((code, index) => (
                            <div key={index}>{code}</div>
                        ))}
                    </div>

                    <Divider />

                    <BPButton
                        as="a"
                        href={buildUrl(AppRoutes.ManageTwoFactor, {})}
                        primary
                    >
                        Continue
                    </BPButton>
                </Segment>
            </Layout>
        );
    }

    if (!setupInfo) {
        return (
            <Layout>
                <Header as="h1">Configure authenticator app</Header>
                <Message error>
                    Unable to load authenticator setup information.
                </Message>
            </Layout>
        );
    }

    return (
        <Layout>
            <Header as="h1">Configure authenticator app</Header>

            <Message info>
                <p>To use an authenticator app go through the following steps:</p>
            </Message>

            <Grid stackable>
                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3">Step 1: Download an authenticator app</Header>
                        <p>Download a two-factor authenticator app like Microsoft Authenticator for:</p>
                        <List bulleted>
                            <List.Item><a href="https://go.microsoft.com/fwlink/?Linkid=825071" target="_blank" rel="noopener noreferrer">Android</a></List.Item>
                            <List.Item><a href="https://go.microsoft.com/fwlink/?Linkid=825072" target="_blank" rel="noopener noreferrer">iOS</a></List.Item>
                        </List>
                    </Segment>
                </Grid.Column>

                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3">Step 2: Scan the QR Code</Header>
                        <p>Scan this QR code or enter the key manually:</p>

                        {/* QR Code placeholder - in a real implementation you'd generate this */}
                        <div style={{ border: '1px solid #ccc', padding: '1rem', textAlign: 'center', marginBottom: '1rem' }}>
                            <div style={{ width: '150px', height: '150px', backgroundColor: '#f0f0f0', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <QRCodeSVG value={setupInfo.authenticatorUri} />
                            </div>
                        </div>

                        <p><strong>Manual entry key:</strong></p>
                        <div style={{ backgroundColor: '#f8f9fa', padding: '0.5rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {setupInfo.sharedKey}
                        </div>
                    </Segment>
                </Grid.Column>
            </Grid>

            <Segment>
                <Header as="h3">Step 3: Verify the code</Header>
                <p>Once you have scanned the QR code or input the key above, your two-factor authentication app will provide you with a unique code. Enter the code in the confirmation box below.</p>

                {enableError && (
                    <Message error>
                        {enableError}
                    </Message>
                )}

                <BPForm
                    initialValues={initialValues}
                    vaildationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    hideButtons={true}
                >
                    {(formProps) => (
                        <>
                            <AuthField
                                name="code"
                                label="Verification Code"
                                autoComplete="off"
                                placeholder="Enter the 6-digit code from your authenticator app"
                            />

                            <Divider />

                            <BPButton
                                type="submit"
                                primary
                                size="large"
                                disabled={formProps.isSubmitting}
                                loading={formProps.isSubmitting}
                            >
                                Verify
                            </BPButton>
                        </>
                    )}
                </BPForm>
            </Segment>
        </Layout>
    );
};